import { useAtom, useAtomValue, useSetAtom } from "jotai";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoGridDocuments from "components/PhotoGridDocuments";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";

import { additionalDocumentsAtom } from "atoms/additionalDocuments";
import { clientConfigAtom } from "atoms/clientConfig";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { Checkpoint } from "types/Checkpoint";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

import { documentToImages } from "./documentToImages";
import { useStyles } from "./styles";

export const AdditionalDocuments = () => {
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);

  const { classes } = useStyles();
  const { currentProgressStep, onTrackAndNext, continueCTA } = usePageRouting();

  const { additionalDocuments: additionalDocumentsConfig } = useAtomValue(clientConfigAtom);
  const { documents, minPhotos } = additionalDocumentsConfig || { minPhotos: 0, documents: [] };

  const { t } = useClientTranslation();

  const [additionalDocuments, setAdditionalDocuments] = useAtom(additionalDocumentsAtom);
  const toggleDrawer = useSetAtom(pageDrawerAtom);

  const canContinue = minPhotos === 0 || additionalDocuments.length >= minPhotos;

  const documentsImages = documents.flatMap(documentToImages).filter((document) => document);

  const onDrop = (files: File[]) => {
    files.forEach(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `additional photo to upload number ${index + 1}`,
        type: Photo.Additional,
      });

      setAdditionalDocuments((photos) => [...photos, image]);
    });
  };

  return (
    <PageLayout
      currentStep={currentProgressStep}
      title={t("additional_documents.header")}
      dataTestId="additionalDocuments"
      required={minPhotos > 0}
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop}>
            <Button typeVariant={canContinue ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {canContinue && (
            <Button
              onClick={onTrackAndNext({
                name: "Additional documents",
                properties: { numberOfDocuments: additionalDocuments.length },
              })}
            >
              {continueCTA}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={8}>
        <Flex gap={24}>
          <Text sizeVariant="md" as="div">
            {t("additional_documents.instruction")}
          </Text>
          {Boolean(additionalDocuments.length) && (
            <Button typeVariant="link" className={classes.explanationButton} onClick={toggleDrawer}>
              {t("global.view_examples")}
            </Button>
          )}

          {Boolean(additionalDocuments.length) ? (
            <PhotoGrid images={additionalDocuments} deleteFromAtom={setAdditionalDocuments} />
          ) : (
            <PhotoGridDocuments images={documentsImages} />
          )}
        </Flex>
      </Flex>
      <BottomDrawer>
        <PhotoGridDocuments images={documentsImages} />
      </BottomDrawer>
    </PageLayout>
  );
};
